import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CheckCircle2, Circle, Copy, ExternalLink, Share2, Rocket, Smartphone, KeyRound, MapPin, CreditCard, CalendarCheck,
  ChevronDown, AlertTriangle, SlidersHorizontal, LifeBuoy,
} from "lucide-react";
import DashboardLayout from "../layouts/DashboardLayout";
import HelpArticleCard from "../components/help/HelpArticleCard";
import api from "../api";
import ShareBookingLinkModal from "../components/booking/ShareBookingLinkModal";

// Setup checklist + patient-booking guide for an org admin (most useful for a
// doctor who just signed up for the free trial). Step state comes from the
// backend (organization/onboarding_routes.py), which ticks steps off from real data.

const PATIENT_STEPS = [
  { icon: Smartphone, title: "Open your link", text: "Patients open your booking link, or find you by city and specialty on the MindConnect booking site." },
  { icon: KeyRound, title: "Sign in with their mobile", text: "They enter their mobile number and a one-time code. No password, no app to install." },
  { icon: MapPin, title: "Pick a visit and a time", text: "Clinic visit or video consultation, the location (if you have more than one), then a free slot from your schedule." },
  { icon: CreditCard, title: "Pay online or at the clinic", text: "They pay online by UPI, card or netbanking, or choose Pay at Clinic if you allow it." },
  { icon: CalendarCheck, title: "Booked", text: "The appointment appears in your Appointments and Calendar straight away." },
];

const FAQS = [
  { q: "Where do I see my bookings?", a: "Appointments lists every booking; Calendar shows them by day and week. New patient bookings appear there immediately." },
  { q: "How do I see a patient on the day?", a: "Open the appointment from Appointments and click Start Consultation (it opens 15 minutes before the slot). Write your notes, prescription and tests; the patient can download the prescription from their account." },
  { q: "What do patients see after booking?", a: "In My Appointments on the booking site they can reschedule, join a video consultation, upload reports, read your prescription, message you and download their invoice." },
  { q: "When do I get paid for online bookings?", a: "Online payments are paid out to your bank account after each completed consultation, minus the payment gateway fee. Finance shows what you've earned and what's been paid out." },
  { q: "I booked a patient myself. Why is it pending?", a: "Appointments you create are sent to the patient to accept (and pay, if you chose online collection). Once they accept, it's confirmed." },
  { q: "Can patients cancel or move their booking?", a: "They can reschedule to another free slot of yours at the same location, as long as the visit hasn't started." },
];

function copy(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(() => toast.success("Link copied"), () => toast.error("Couldn't copy - select the link and copy it."));
  }
}

const hasSchedule = (ws) =>
  !!ws && typeof ws === "object" && Object.values(ws).some((d) => d && typeof d === "object" && d.is_working && (d.shifts || []).length);


function LinkCard({ b, onShare }) {
  return (
    <div className="rounded-xl border border-secondary-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-secondary-900">{b.doctor_name}</p>
          <p className="text-sm text-primary-700 break-all">{b.link}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => copy(b.link)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-secondary-300 text-sm hover:bg-secondary-50">
            <Copy className="w-4 h-4" /> Copy
          </button>
          <button type="button" onClick={() => onShare(b)} data-label="Share Booking Link"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700">
            <Share2 className="w-4 h-4" /> WhatsApp / SMS
          </button>
          <a href={b.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-secondary-300 text-sm hover:bg-secondary-50">
            <ExternalLink className="w-4 h-4" /> Preview
          </a>
        </div>
      </div>
      {!b.has_schedule && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-amber-700">
          <AlertTriangle className="w-4 h-4" /> No availability set yet, so patients won't see any slots. Set the weekly schedule first.
        </p>
      )}
    </div>
  );
}

export default function GettingStarted() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const linksRef = useRef(null);
  // Bigger practices search for a doctor's link instead of a card per doctor.
  const [linkSearch, setLinkSearch] = useState("");
  const [linkResults, setLinkResults] = useState(null);
  const [sharing, setSharing] = useState(null);  // {link, doctor_name?, doctor_user_id?}

  useEffect(() => {
    const term = linkSearch.trim();
    if (!term) { setLinkResults(null); return undefined; }
    const timer = setTimeout(() => {
      api.fetchDoctors({ search: term, limit: 10 })
        .then((res) => setLinkResults((res.data?.data || []).filter((d) => d.is_active !== false && d.booking_link).map((d) => ({
          doctor_name: d.name, doctor_user_id: d.doctor_id, link: d.booking_link, has_schedule: hasSchedule(d.weekly_schedule),
        }))))
        .catch(() => setLinkResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [linkSearch]);

  // Settings guide: the help articles marked as settings, for this clinic's
  // features and this user's role (backend help_content/articles.json).
  const [settingsGuide, setSettingsGuide] = useState([]);
  useEffect(() => {
    api.fetchHelpArticles()
      .then((r) => setSettingsGuide((r.data?.articles || []).filter((a) => a.setting)))
      .catch(() => setSettingsGuide([]));
  }, []);
  const settingGroups = [...new Set(settingsGuide.map((a) => a.setting.group))];

  const load = () => api.fetchOnboardingStatus().then((r) => setStatus(r.data)).catch(() => setError("Couldn't load your setup checklist."));
  useEffect(() => { load(); }, []);

  const runAction = async (s) => {
    const { path, doctor_user_id } = s.action;
    if (path === "#booking-link") {
      linksRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (path.startsWith("/doctor-edit") && doctor_user_id) {
      // DoctorEdit expects the doctor's list row as navigation state.
      try {
        const res = await api.fetchDoctors({ limit: 100 });
        const row = (res.data?.data || []).find((d) => d.doctor_id === doctor_user_id);
        if (row) return navigate("/doctor-edit/", { state: row });
      } catch {
        // fall through to the doctor list
      }
      return navigate("/doctor");
    }
    navigate(path);
  };

  const mark = async (s, done) => {
    try {
      const res = await api.markOnboardingStep(s.id, done);
      setStatus(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't update this step.");
    }
  };

  if (error) return <DashboardLayout><p className="p-8 text-red-600">{error}</p></DashboardLayout>;
  if (!status) return <DashboardLayout><p className="p-8 text-secondary-500">Loading…</p></DashboardLayout>;
  if (!status.applicable) {
    return <DashboardLayout><p className="p-8 text-secondary-500">The setup guide is for clinics and practices.</p></DashboardLayout>;
  }

  const pct = status.total ? Math.round((status.completed / status.total) * 100) : 0;
  const nextStep = status.steps.find((s) => !s.done && !s.optional);
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-8 pb-12">
        {/* Header + progress */}
        <div className="rounded-2xl bg-gradient-to-br from-primary-600 to-primary-500 text-white p-6 md:p-8 shadow-lg">
          <div className="flex items-start gap-3">
            <Rocket className="w-8 h-8 shrink-0" />
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold">Getting started</h1>
              <p className="text-white/85 mt-1">
                {status.completed === status.total
                  ? "You're all set. Your practice is ready to take bookings."
                  : `A few steps to get ${status.org_name} ready to see patients. Each one ticks itself off as you finish it.`}
              </p>
            </div>
          </div>
          <div className="mt-5">
            <div className="flex justify-between text-sm text-white/90 mb-1">
              <span>{status.completed} of {status.total} done</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/25 overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        {/* Checklist */}
        <section>
          <h2 className="text-lg font-semibold text-secondary-900 mb-3">Your setup checklist</h2>
          <ol className="space-y-3">
            {status.steps.map((s, i) => {
              const isNext = nextStep && nextStep.id === s.id;
              return (
                <li key={s.id} className={`rounded-xl border bg-white p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${isNext ? "border-primary-400 ring-1 ring-primary-200 shadow-sm" : "border-secondary-200"}`}>
                  <div className="flex items-start gap-3 flex-1">
                    {s.done ? <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0 mt-0.5" /> : <Circle className="w-6 h-6 text-secondary-300 shrink-0 mt-0.5" />}
                    <div>
                      <p className={`font-semibold ${s.done ? "text-secondary-500 line-through" : "text-secondary-900"}`}>
                        {i + 1}. {s.title}
                        {s.optional && <span className="ml-2 text-xs font-medium text-secondary-400 no-underline">Optional</span>}
                        {isNext && <span className="ml-2 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold px-2 py-0.5">Next</span>}
                      </p>
                      <p className="text-sm text-secondary-500 mt-0.5">{s.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:shrink-0 pl-9 sm:pl-0">
                    <button type="button" onClick={() => runAction(s)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${s.done ? "border border-secondary-300 text-secondary-700 hover:bg-secondary-50" : "bg-primary-600 text-white hover:bg-primary-700"}`}>
                      {s.action.label}
                    </button>
                    {s.manual && (
                      <button type="button" onClick={() => mark(s, !s.done)} className="px-3 py-2 rounded-lg text-sm text-secondary-600 hover:bg-secondary-100">
                        {s.done ? "Undo" : "Mark as done"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        {/* Every setting a practice can change, what it does, and where it is */}
        {settingGroups.length > 0 && (
          <section id="settings-guide" className="scroll-mt-24">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-secondary-900">
              <SlidersHorizontal className="w-5 h-5 text-primary-600" /> Your settings, explained
            </h2>
            <p className="text-sm text-secondary-500 mb-4">
              Everything you can adjust for your practice, what it changes for you and your patients, and where to find it. Open any one for the steps.
            </p>
            <div className="grid gap-6 md:grid-cols-2">
              {settingGroups.map((g) => (
                <div key={g} className="space-y-2 min-w-0">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-secondary-500">{g}</h3>
                  {settingsGuide.filter((a) => a.setting.group === g).map((a) => <HelpArticleCard key={a.id} article={a} compact />)}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Booking links */}
        <section ref={linksRef} id="booking-link" className="scroll-mt-24">
          <h2 className="text-lg font-semibold text-secondary-900">Your patient booking link{status.booking_links.length > 1 ? "s" : ""}</h2>
          <p className="text-sm text-secondary-500 mb-3">
            Share this anywhere patients find you: WhatsApp, your Google Business profile, Instagram bio, website, or a printout at the front desk.
          </p>
          {status.total_doctors === 0 ? (
            <div className="rounded-xl border border-dashed border-secondary-300 p-5 text-sm text-secondary-600">
              Add a doctor first; each doctor gets their own booking link.
            </div>
          ) : (
            <div className="grid gap-3">
              {status.total_doctors > status.booking_links.length && (
                <div>
                  <label htmlFor="link-search" className="block text-sm font-medium text-secondary-700 mb-1">
                    Find a doctor's link ({status.total_doctors} doctors)
                  </label>
                  <input id="link-search" type="search" value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)}
                    placeholder="Search by doctor name or specialty"
                    className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm" />
                  <p className="text-xs text-secondary-500 mt-1">
                    Every doctor's link is also on the <button type="button" onClick={() => navigate("/doctor")} className="text-primary-600 underline">Clinicians</button> page (⋮ → Copy booking link).
                  </p>
                </div>
              )}
              {status.portal_url && (
                <div className="rounded-xl border border-primary-200 bg-primary-50/40 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-secondary-900">Your clinic's page (all doctors)</p>
                      <p className="text-sm text-primary-700 break-all">{status.portal_url}</p>
                      <p className="text-xs text-secondary-500 mt-1">
                        {status.portal_private
                          ? "Only your clinic is shown here, and your doctors don't appear in MindConnect's public search."
                          : "Only your clinic is shown here. Each doctor's link below also shows your clinic first."}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => copy(status.portal_url)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-secondary-300 text-sm hover:bg-secondary-50">
                        <Copy className="w-4 h-4" /> Copy
                      </button>
                      <button type="button" onClick={() => setSharing({ link: status.portal_url })} data-label="Share Clinic Link"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700">
                        <Share2 className="w-4 h-4" /> WhatsApp / SMS
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {(linkResults ?? status.booking_links).map((b) => <LinkCard key={b.doctor_user_id} b={b} onShare={setSharing} />)}
              {linkResults && linkResults.length === 0 && (
                <p className="text-sm text-secondary-500">No doctor matches "{linkSearch}".</p>
              )}
              {!status.portal_private && <p className="text-xs text-secondary-500">
                Patients can also find you by city and specialty at{" "}
                <a href={status.patient_app_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">{status.patient_app_url}</a>.
              </p>}
            </div>
          )}
        </section>

        {/* How patients book */}
        <section>
          <h2 className="text-lg font-semibold text-secondary-900 mb-3">How your patients book</h2>
          <ol className="grid gap-3 md:grid-cols-5">
            {PATIENT_STEPS.map((p, i) => (
              <li key={p.title} className="rounded-xl border border-secondary-200 bg-white p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-sm font-bold flex items-center justify-center">{i + 1}</span>
                  <p.icon className="w-5 h-5 text-primary-600" />
                </div>
                <p className="font-semibold text-secondary-900 text-sm">{p.title}</p>
                <p className="text-xs text-secondary-500 mt-1">{p.text}</p>
              </li>
            ))}
          </ol>
          <p className="text-sm text-secondary-500 mt-3">
            Tip: open your own link on your phone and try it as a patient. It's the quickest way to see exactly what they see.
          </p>
        </section>

        {/* FAQ */}
        <section>
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <h2 className="text-lg font-semibold text-secondary-900">Common questions</h2>
            <button type="button" onClick={() => navigate("/help")} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:underline">
              <LifeBuoy className="w-4 h-4" /> Ask anything in Help
            </button>
          </div>
          <div className="rounded-xl border border-secondary-200 bg-white divide-y divide-secondary-100">
            {FAQS.map((f, i) => (
              <div key={f.q}>
                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
                  <span className="font-medium text-secondary-800 text-sm">{f.q}</span>
                  <ChevronDown className={`w-4 h-4 text-secondary-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="px-4 pb-4 text-sm text-secondary-600">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>
      </div>
      {sharing && (
        <ShareBookingLinkModal link={sharing.link} doctorName={sharing.doctor_name} doctorUserId={sharing.doctor_user_id}
          orgName={status.org_name} onClose={() => setSharing(null)} />
      )}
    </DashboardLayout>
  );
}
